import React, { ReactNode, useEffect } from 'react';
import ApolloClient from 'apollo-client';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import { MockApolloClient } from 'mock-apollo-client';
import MenuItem from '@material-ui/core/MenuItem';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import { captureException } from '@sentry/browser';
import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles';

import { CurrentUser } from '../../../rematch-models/user';
import { ReportedCoachingVisit } from '../../../models/Sessions';
import {
  CoachingVisitToReportQuery,
  useCoachingVisitToReportLazyQuery,
} from '../documents/CoachingVisitToReport.generated';
import { colors } from '../../../themes/colors';
import { formatScheduledTimeForDisplay } from './helpers';
import { SELECT_PLACEHOLDER_TEXT, TestID } from './constants';
import usePrevious from '../../../hooks/usePrevious';

const styles = () =>
  createStyles({
    selectRoot: {
      flex: '1 1 auto',
      display: 'flex',
      overflow: 'hidden',
    },
    muiRoot: {
      display: 'block',
      backgroundColor: 'transparent !important',
    },
    selectInput: {
      color: colors.gray18,
      fontSize: '14px',
      maxHeight: 40,
      flex: '1 1 auto',
      minWidth: 0,
      fontFamily: 'Avenir-Medium',
      borderRadius: 6,
      '& input': {
        cursor: 'pointer',
      },
      '& fieldset': {
        maxHeight: 40,
        borderColor: '#E9EEF4',
        borderWidth: 1,
      },
      '&:hover fieldset': {
        borderColor: `${colors.blue6} !important`,
      },
      '& .Mui-focused fieldset': {
        maxHeight: 40,
        borderColor: `${colors.blue6} !important`,
      },
      '& div.Mui-error fieldset': {
        borderColor: '#e53e3e !important',
      },
      '& .MuiButtonBase-root.MuiIconButton-root': {
        outline: 'none',
        position: 'absolute',
        right: 0,
        marginTop: 2,
        color: colors.gray16,
      },
    },
    menuItemStyle: {
      fontSize: 12,
      fontWeight: 500,
      color: colors.black2,
    },
  });

export type Props = {
  disabled: boolean;
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
  memberUuid?: string;
  onChange: (coachingVisit: ReportedCoachingVisit) => void;
  onCompleted: (coachingVisits: CoachingVisitToReportQuery) => void;
  selected?: ReportedCoachingVisit;
  user: CurrentUser;
} & WithStyles;

const ReportedCoachingVisitSelector: React.FC<Props> = ({
  classes,
  disabled,
  memberClient,
  memberUuid,
  onChange,
  onCompleted,
  selected,
  user,
}) => {
  const handleCompleted = (data: CoachingVisitToReportQuery) => {
    onCompleted(data);
  };

  const [fetchCoachingVisitsToReport, { data, loading }] = useCoachingVisitToReportLazyQuery({
    client: memberClient,
    fetchPolicy: 'network-only',
    onCompleted: handleCompleted,
    onError: captureException,
    variables: {
      memberUuid,
    },
  });

  const previousMemberUuid = usePrevious(memberUuid);

  useEffect(() => {
    if (loading || disabled || !memberUuid || previousMemberUuid === memberUuid || data) return;

    fetchCoachingVisitsToReport();
  }, [data, disabled, loading, fetchCoachingVisitsToReport, memberUuid, previousMemberUuid]);

  const handleChange = (event) => {
    if (!memberUuid || loading) return;

    const coachingVisit = data?.coachingVisitToReport?.find(
      (coachingVisit) => coachingVisit?.uuid === event.target.value,
    );

    if (coachingVisit) {
      onChange(coachingVisit as ReportedCoachingVisit);
    }
  };

  let menuItems = [] as ReactNode[];

  if (selected) {
    menuItems = [
      <MenuItem className={classes.menuItemStyle} key={selected.uuid} value={selected.uuid}>
        {formatScheduledTimeForDisplay({
          scheduledTime: selected.scheduledTime,
          user,
        })}
      </MenuItem>,
    ];
  }

  if (!disabled) {
    menuItems =
      data?.coachingVisitToReport?.reduce((accumulator: ReactNode[], coachingVisit) => {
        if (!coachingVisit || coachingVisit.uuid === selected?.uuid) return accumulator;

        const menuItem = (
          <MenuItem
            className={classes.menuItemStyle}
            key={coachingVisit.uuid}
            value={coachingVisit.uuid}
          >
            {formatScheduledTimeForDisplay({
              scheduledTime: coachingVisit.scheduledTime,
              user,
            })}
          </MenuItem>
        );

        return [...accumulator, menuItem];
      }, menuItems) ?? menuItems;
  }

  useEffect(() => {
    if (data?.coachingVisitToReport?.length === 1) {
      onChange(data.coachingVisitToReport[0] as ReportedCoachingVisit);
    }
  }, [data, onChange]);

  return (
    <div className={classes.selectRoot}>
      <Select
        className={classes.selectInput}
        disabled={disabled}
        id="reported-coaching-visit-selector"
        input={<OutlinedInput data-testid={TestID} />}
        classes={{ root: classes.muiRoot }}
        onChange={handleChange}
        value={selected?.uuid || SELECT_PLACEHOLDER_TEXT}
      >
        {menuItems}

        <MenuItem style={{ display: 'none' }} value={SELECT_PLACEHOLDER_TEXT}>
          {SELECT_PLACEHOLDER_TEXT}
        </MenuItem>
      </Select>
    </div>
  );
};

export default withStyles(styles)(ReportedCoachingVisitSelector);
